import { useEffect, useRef, useState } from 'react';
import { PaperPlaneTilt, Stop } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const MAX_HEIGHT_PX = 200;

/**
 * The assistant composer: autosizing textarea, Enter to send,
 * Shift+Enter for a newline, and a stop button while streaming.
 */
export function AssistantComposer({
  onSend,
  onStop,
  streaming,
  disabled,
  placeholder,
}: {
  onSend: (text: string) => void;
  onStop: () => void;
  streaming: boolean;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT_PX)}px`;
  };

  useEffect(() => {
    adjustHeight();
  }, [value]);

  const submit = () => {
    const text = value.trim();
    if (!text || streaming || disabled) return;
    setValue('');
    onSend(text);
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  return (
    <div
      className={cn(
        'rounded-xl border bg-card shadow-sm transition-colors',
        'focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/15',
      )}
    >
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        rows={1}
        disabled={disabled}
        placeholder={
          placeholder ?? 'Ask about live data or how the system works…'
        }
        className="max-h-[200px] w-full resize-none bg-transparent px-3.5 pt-3 text-sm outline-none placeholder:text-muted-foreground/70 disabled:opacity-50"
        data-testid="assistant-composer"
      />
      <div className="flex items-center justify-between px-2.5 pb-2">
        <span className="px-1 text-[10px] text-muted-foreground/60">
          Enter to send · Shift+Enter for a new line
        </span>
        {streaming ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onStop}
            className="h-7 gap-1.5 px-2.5 text-xs"
          >
            <Stop weight="fill" className="h-3 w-3" />
            Stop
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            onClick={submit}
            disabled={disabled || value.trim().length === 0}
            className="h-7 gap-1.5 px-2.5 text-xs"
          >
            <PaperPlaneTilt weight="fill" className="h-3 w-3" />
            Send
          </Button>
        )}
      </div>
    </div>
  );
}
